import { api } from '@/lib/api';
import type { PerformanceSummary, PerformanceReview, PerformanceGoal } from '@pms/shared';

export interface SummaryParams {
  period_type: 'monthly' | 'quarterly' | 'annual';
  year: number;
  month?: number;
  quarter?: number;
}

export interface CreateReviewPayload {
  period_type: 'monthly' | 'quarterly' | 'annual';
  period_year: number;
  period_month?: number | null;
  period_quarter?: number | null;
  overall_rating: number;
  attendance_rating?: number | null;
  task_rating?: number | null;
  behaviour_rating?: number | null;
  initiative_rating?: number | null;
  strengths?: string | null;
  improvements?: string | null;
  remarks?: string | null;
  status?: 'draft' | 'submitted';
}

export interface CreateGoalPayload {
  title: string;
  description?: string | null;
  category?: 'attendance' | 'tasks' | 'skills' | 'behaviour' | 'custom';
  target_value?: number | null;
  actual_value?: number | null;
  unit?: string | null;
  deadline?: string | null;
  status?: 'pending' | 'in_progress' | 'achieved' | 'missed' | 'cancelled';
}

export interface UpdateGoalPayload extends Partial<CreateGoalPayload> {}

export const performanceService = {
  getSummary: async (userId: string, params: SummaryParams): Promise<PerformanceSummary> => {
    const qs = new URLSearchParams({ period_type: params.period_type, year: String(params.year) });
    if (params.month   !== undefined) qs.set('month',   String(params.month));
    if (params.quarter !== undefined) qs.set('quarter', String(params.quarter));
    const res = await api.get<{ data: PerformanceSummary }>(`/performance/staff/${userId}/summary?${qs}`);
    return res.data.data;
  },

  listReviews: async (userId: string, page = 1, limit = 10): Promise<{ data: PerformanceReview[]; total: number }> => {
    const res = await api.get<{ data: PerformanceReview[]; pagination: { total: number } }>(
      `/performance/staff/${userId}/reviews?page=${page}&limit=${limit}`,
    );
    return { data: res.data.data, total: res.data.pagination.total };
  },

  createReview: async (userId: string, payload: CreateReviewPayload): Promise<PerformanceReview> => {
    const res = await api.post<{ data: PerformanceReview }>(`/performance/staff/${userId}/reviews`, payload);
    return res.data.data;
  },

  updateReview: async (userId: string, id: string, payload: Partial<CreateReviewPayload> & { status?: string }): Promise<PerformanceReview> => {
    const res = await api.patch<{ data: PerformanceReview }>(`/performance/staff/${userId}/reviews/${id}`, payload);
    return res.data.data;
  },

  deleteReview: async (userId: string, id: string): Promise<void> => {
    await api.delete(`/performance/staff/${userId}/reviews/${id}`);
  },

  listGoals: async (userId: string): Promise<PerformanceGoal[]> => {
    const res = await api.get<{ data: PerformanceGoal[] }>(`/performance/staff/${userId}/goals`);
    return res.data.data;
  },

  createGoal: async (userId: string, payload: CreateGoalPayload): Promise<PerformanceGoal> => {
    const res = await api.post<{ data: PerformanceGoal }>(`/performance/staff/${userId}/goals`, payload);
    return res.data.data;
  },

  updateGoal: async (userId: string, id: string, payload: UpdateGoalPayload): Promise<PerformanceGoal> => {
    const res = await api.patch<{ data: PerformanceGoal }>(`/performance/staff/${userId}/goals/${id}`, payload);
    return res.data.data;
  },

  deleteGoal: async (userId: string, id: string): Promise<void> => {
    await api.delete(`/performance/staff/${userId}/goals/${id}`);
  },
};
